function transformDataToTree(list, existingTree = {}) {
  const treeMap = new Map(Object.entries(existingTree)); // Retain existing tree structure

  const getItemId = (item) => item?.id || item?.itemId;

  const getParentWithId = (parent) => {
    if (!parent) return null;
    return {
      ...parent,
      id: parent.itemId
    };
  };

  list.forEach((item) => {
    treeMap.set(getItemId(item), {
      index: getItemId(item),
      data: item.name,
      hasChildren: !item?.parent ? true : false,
      children: item?.children || [],
      parent: getParentWithId(item?.parent),
      fullData: item
    });
  });

  // Assigning children to their parents
  list.forEach((item) => {
    if (item?.parent) {
      const parentId = item.parent.itemId;
      const parentNode = treeMap.get(parentId) || { children: [] };

      if (!parentNode.children.includes(getItemId(item))) {
        parentNode.children.push(getItemId(item));
      }
      parentNode.hasChildren = true;

      treeMap.set(parentId, parentNode);
    } else {
      const rootNode = treeMap.get('root') || {
        index: 'root',
        hasChildren: true,
        children: [],
        data: 'Root'
      };
      rootNode.children = [...new Set([...rootNode.children, getItemId(item)])];
      treeMap.set('root', rootNode);
    }
  });

  return Object.fromEntries(treeMap);
}

/**
 * Removes a node (and optionally its descendants) from the tree object based on the given item id.
 * @param {Object} tree - The tree object as returned by transformDataToTree.
 * @param {string|number} removeId - The id of the item to remove.
 * @param {boolean} [removeDescendants=true] - Whether to remove all descendants of the node as well.
 * @returns {{ tree: Object, selectId: string|number|null }} - The new tree object with the item removed and the id to select next.
 */
function removeItemFromTree(tree, removeId, removeDescendants = true) {
  if (!tree || !removeId) return { tree: { ...tree }, selectId: null };
  const newTree = { ...tree };

  // Helper to recursively collect all descendant ids
  function collectDescendants(id, acc = []) {
    const node = newTree[id];
    if (node && node.children && node.children.length) {
      node.children.forEach((childId) => {
        acc.push(childId);
        collectDescendants(childId, acc);
      });
    }
    return acc;
  }

  // Remove descendants if needed
  let idsToRemove = [removeId];
  if (removeDescendants && newTree[removeId]) {
    idsToRemove = [removeId, ...collectDescendants(removeId)];
  }

  // Find parent and siblings for selectId logic
  const parentId = newTree[removeId]?.parent?.itemId || 'root';
  const siblings = (newTree[parentId]?.children || []).slice();
  const idx = siblings.indexOf(removeId);

  // Determine selectId: previous sibling, next sibling, or parent/root
  let selectId = null;
  if (idx > 0) {
    selectId = siblings[idx - 1];
  } else if (idx === 0 && siblings.length > 1) {
    selectId = siblings[1];
  } else {
    selectId = parentId !== 'root' ? parentId : null;
  }

  // Remove references from parent or root
  if (newTree[parentId] && newTree[parentId].children) {
    newTree[parentId].children = newTree[parentId].children.filter(
      (itemId) => itemId !== removeId
    );
    if (newTree[parentId].children.length === 0) {
      newTree[parentId].hasChildren = false;
    }
  }

  // Delete the nodes
  idsToRemove.forEach((id) => {
    delete newTree[id];
  });

  return { tree: newTree, selectId };
}

export { transformDataToTree, removeItemFromTree };
